import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { FileTextIcon as FileText, WarningIcon as Warning, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, UploadSimpleIcon as UploadSimple, PencilSimpleIcon as PencilSimple, FloppyDiskIcon as FloppyDisk } from "@phosphor-icons/react";

/**
 * Per-puppy sale contract + sale snapshot — rendered on the Dog detail
 * Overview tab for the dog's OWNER (the breeder selling the puppy).
 *
 * Two stacked sub-cards:
 *
 *  1. Sale snapshot — buyer name + price + deposit + balance + go-home
 *     date + notes. Just a tiny breeder-facing data sheet so they can
 *     see every sale's terms at a glance.
 *
 *  2. Per-puppy contract — PDF upload + click-wrap sign by breeder,
 *     then handoff to the buyer for their click-wrap. Status banner
 *     shows where in the flow it is.
 *
 * Buyer-side view is intentionally narrower: they only see the
 * contract panel (NOT the sale snapshot, which is breeder-private)
 * and only after the breeder has signed. The button label switches
 * to "Sign as buyer" in that case.
 */
export default function SaleContractCard({ dog, isOwner, currentUserEmail }) {
  const [snapshot, setSnapshot] = useState(null);
  const [contract, setContract] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snapDraft, setSnapDraft] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [ack, setAck] = useState(false);
  const [assignEmail, setAssignEmail] = useState("");
  const fileRef = useRef(null);

  const dogId = dog?.id;

  const isBuyerParty =
    !isOwner &&
    !!contract &&
    ((currentUserEmail || "").toLowerCase() === (contract.buyer_email || "").toLowerCase());

  const load = async () => {
    if (!dogId) return;
    try {
      const [c, s] = await Promise.all([
        api.get(`/contracts/dog/${dogId}`, { silent: true }),
        isOwner
          ? api.get(`/contracts/dog/${dogId}/sale-snapshot`, { silent: true })
          : Promise.resolve({ data: null }),
      ]);
      setContract(c.data);
      setSnapshot(s.data);
      setAssignEmail(c.data?.buyer_email || "");
    } catch {
      // Buyer who isn't yet assigned will 403 — that's expected.
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogId, isOwner]);

  // Owner-only and buyer-of-this-contract are the only renderers.
  if (!dogId) return null;
  if (!isOwner && !contract) return null;

  const onUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/contracts/dog/${dogId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContract(res.data);
      toast.success("Contract uploaded. Sign it to send to your buyer.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onAssign = async () => {
    if (!assignEmail.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/contracts/dog/${dogId}/assign-buyer`, {
        buyer_email: assignEmail.trim(),
      });
      toast.success("Buyer linked. They'll see the contract on their account.");
      setContract((c) => (c ? { ...c, ...res.data } : c));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSign = async () => {
    if (!legalName.trim() || !ack) {
      return toast.error("Type your legal name and acknowledge the venue notice.");
    }
    setBusy(true);
    try {
      const endpoint = isOwner ? "sign-breeder" : "sign-buyer";
      await api.post(`/contracts/dog/${dogId}/${endpoint}`, {
        legal_name: legalName.trim(),
        venue_disclaimer_acknowledged: true,
      });
      toast.success(isOwner ? "Contract signed. Send the link to your buyer." : "Contract signed.");
      setSignOpen(false);
      setLegalName("");
      setAck(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Status banner that reads back the next required step in plain English.
  const banner = (() => {
    if (!contract) {
      return {
        tone: "warn",
        text: "No signed contract on file. Buyers are 80% less likely to dispute when this is uploaded.",
      };
    }
    if (contract.status === "draft" && isOwner) return { tone: "info", text: "Draft uploaded. Sign it to lock the terms, then send the buyer the link." };
    if (contract.status === "breeder_signed" && isOwner) return { tone: "info", text: "You've signed. Waiting on the buyer's signature." };
    if (contract.status === "breeder_signed" && isBuyerParty) return { tone: "warn", text: "The breeder has signed. Review the PDF and add your signature." };
    if (contract.status === "fully_executed") return { tone: "ok", text: "Fully executed contract on file. Both parties have signed." };
    return { tone: "info", text: "" };
  })();

  return (
    <div className="space-y-4" data-testid="sale-contract-block">
      {isOwner && (
        <SaleSnapshotPanel
          dogId={dogId}
          snapshot={snapshot}
          setSnapshot={setSnapshot}
          snapDraft={snapDraft}
          setSnapDraft={setSnapDraft}
        />
      )}

      <div
        className="bg-white border border-[#EAEAEA] rounded-xl p-5"
        data-testid="sale-contract-card"
      >
        <div className="flex items-start gap-4">
          <FileText size={28} weight="duotone" className="text-gold mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <h3 className="font-spectral text-lg">
                {isOwner ? "Sale contract for this puppy" : "Your sale contract"}
              </h3>
              {contract && (
                <span
                  data-testid="contract-status-pill"
                  className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 whitespace-nowrap border ${
                    contract.status === "fully_executed"
                      ? "bg-[#E7F8EC] text-[#0F7B3E] border-[#BFE8C9]"
                      : contract.status === "breeder_signed"
                      ? "bg-[#FBF7EC] text-[#8A6A00] border-[#E8D9A6]"
                      : "bg-[#F1F3F8] text-[#3A4670] border-[#CFD6E8]"
                  }`}
                >
                  {contract.status === "fully_executed"
                    ? "Fully executed"
                    : contract.status === "breeder_signed"
                    ? "Awaiting buyer"
                    : "Draft"}
                </span>
              )}
            </div>

            {banner.text && (
              <p
                className={`mt-2 text-[12.5px] px-3 py-2 rounded-lg inline-flex items-start gap-2 leading-snug ${
                  banner.tone === "warn"
                    ? "bg-[#FFF3E0] text-[#8A4A00] border border-[#F4DAA8]"
                    : banner.tone === "ok"
                    ? "bg-[#E7F8EC] text-[#0F7B3E] border border-[#BFE8C9]"
                    : "bg-[#F1F3F8] text-[#3A4670] border border-[#CFD6E8]"
                }`}
                data-testid="contract-banner"
              >
                {banner.tone === "warn" && <Warning size={13} weight="fill" />}
                {banner.tone === "ok" && <CheckCircle size={13} weight="fill" />}
                <span>{banner.text}</span>
              </p>
            )}

            <p className="text-[13px] text-[#3F3F3F] mt-3 leading-relaxed">
              Each puppy needs its own contract because the buyer, price,
              deposit, go-home date and any health/return clauses change
              per sale. Upload your PDF (your lawyer's version) then sign
              it with a click-wrap. Armstrong K9 Registry is a venue, never a party
              to your contract.
            </p>

            {isOwner && (
              <div className="mt-3 rounded-lg border border-dashed border-[#E5E5E5] bg-[#FBFBFB] p-3 text-[12px] text-[#6B6B6B] leading-snug">
                <strong className="text-ink">Required info to include in the PDF:</strong>{" "}
                Puppy reg # ({dog.registration_number || "—"}), buyer's full
                legal name, price, deposit, go-home date, health guarantee
                terms, and any spay/neuter expectations.
              </div>
            )}

            {contract?.pdf_url && (
              <div className="mt-3 flex flex-wrap gap-2 items-center text-sm">
                <a
                  href={contract.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-ink underline hover:text-crimson"
                  data-testid="contract-download-link"
                >
                  View current PDF <ArrowSquareOut size={12} />
                </a>
                <span className="text-[11px] text-[#6B6B6B]">v{contract.version}</span>
              </div>
            )}

            {/* OWNER actions */}
            {isOwner && (
              <div className="mt-4 space-y-3">
                {contract?.status !== "fully_executed" && (
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">
                      {contract ? "Replace contract PDF" : "Upload contract PDF"}
                    </Label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => onUpload(e.target.files?.[0])}
                      data-testid="contract-upload-input"
                      className="text-[12px]"
                    />
                  </div>
                )}

                {contract && contract.status !== "fully_executed" && (
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">
                        Buyer's email (so they can sign)
                      </Label>
                      <Input
                        value={assignEmail}
                        onChange={(e) => setAssignEmail(e.target.value)}
                        placeholder="buyer@example.com"
                        data-testid="contract-buyer-email"
                      />
                    </div>
                    <Button
                      onClick={onAssign}
                      disabled={busy || !assignEmail.trim()}
                      variant="outline"
                      className="rounded-full"
                      data-testid="contract-assign-buyer-btn"
                    >
                      Link buyer
                    </Button>
                  </div>
                )}

                {contract?.status === "draft" && !signOpen && (
                  <Button
                    onClick={() => setSignOpen(true)}
                    className="bg-ink hover:bg-crimson text-white rounded-full"
                    data-testid="contract-open-sign-btn"
                  >
                    <PencilSimple size={14} weight="bold" /> Sign as breeder
                  </Button>
                )}
              </div>
            )}

            {/* BUYER actions */}
            {isBuyerParty && contract?.status === "breeder_signed" && !signOpen && (
              <Button
                onClick={() => setSignOpen(true)}
                className="mt-4 bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="contract-buyer-sign-btn"
              >
                <PencilSimple size={14} weight="bold" /> Sign as buyer
              </Button>
            )}

            {/* Sign click-wrap (shared) */}
            {signOpen && (
              <div className="mt-4 rounded-xl border border-[#E5E5E5] bg-[#FBFBFB] p-4 space-y-3" data-testid="contract-sign-form">
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] block">
                  Your full legal name
                </Label>
                <Input
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="As it appears on government ID"
                  data-testid="contract-sign-legalname"
                />
                <label className="flex items-start gap-2 text-[12.5px] text-[#3F3F3F] leading-snug">
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    data-testid="contract-sign-ack"
                  />
                  <span>
                    I have reviewed the contract above and agree to its terms.
                    I understand Armstrong K9 Registry is a venue providing
                    storage and signing tools, not a party to this contract
                    and not legal counsel.
                  </span>
                </label>
                <div className="flex gap-2">
                  <Button
                    onClick={onSign}
                    disabled={busy || !legalName.trim() || !ack}
                    className="bg-ink hover:bg-crimson text-white rounded-full"
                    data-testid="contract-sign-submit"
                  >
                    Sign & lock
                  </Button>
                  <Button
                    onClick={() => { setSignOpen(false); setLegalName(""); setAck(false); }}
                    variant="outline"
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SaleSnapshotPanel({ dogId, snapshot, setSnapshot, snapDraft, setSnapDraft }) {
  const [busy, setBusy] = useState(false);
  const editing = snapDraft !== null;

  const startEdit = () => {
    setSnapDraft({
      buyer_name: snapshot?.buyer_name || "",
      buyer_email: snapshot?.buyer_email || "",
      price: snapshot?.price || "",
      deposit_paid: snapshot?.deposit_paid || "",
      balance_due: snapshot?.balance_due || "",
      go_home_date: snapshot?.go_home_date || "",
      notes: snapshot?.notes || "",
    });
  };

  const save = async () => {
    if (!snapDraft) return;
    setBusy(true);
    try {
      const body = {
        ...snapDraft,
        price: snapDraft.price ? Number(snapDraft.price) : null,
        deposit_paid: snapDraft.deposit_paid ? Number(snapDraft.deposit_paid) : null,
        balance_due: snapDraft.balance_due ? Number(snapDraft.balance_due) : null,
      };
      const { data } = await api.put(`/contracts/dog/${dogId}/sale-snapshot`, body);
      setSnapshot(data);
      setSnapDraft(null);
      toast.success("Sale snapshot saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-5" data-testid="sale-snapshot-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-spectral text-lg">Sale snapshot</h3>
          <p className="text-[12px] text-[#6B6B6B] mt-1">
            Private to you. Captures the buyer, price, and dates so you can
            scan any sale at a glance.
          </p>
        </div>
        {!editing && (
          <Button
            onClick={startEdit}
            variant="outline"
            size="sm"
            className="rounded-full"
            data-testid="sale-snapshot-edit-btn"
          >
            <PencilSimple size={12} weight="bold" /> {snapshot ? "Edit" : "Add details"}
          </Button>
        )}
      </div>

      {!editing && snapshot && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-[13px]">
          <SnapField label="Buyer" value={snapshot.buyer_name} />
          <SnapField label="Buyer email" value={snapshot.buyer_email} />
          <SnapField label="Go-home" value={snapshot.go_home_date} />
          <SnapField label="Price" value={snapshot.price ? `$${Number(snapshot.price).toFixed(2)}` : null} />
          <SnapField label="Deposit" value={snapshot.deposit_paid ? `$${Number(snapshot.deposit_paid).toFixed(2)}` : null} />
          <SnapField label="Balance due" value={snapshot.balance_due ? `$${Number(snapshot.balance_due).toFixed(2)}` : null} />
          {snapshot.notes && (
            <div className="col-span-2 sm:col-span-3 text-[12.5px] text-[#3F3F3F] bg-[#FBFBFB] border border-[#EAEAEA] rounded-lg p-2 mt-1 leading-snug">
              {snapshot.notes}
            </div>
          )}
        </div>
      )}

      {!editing && !snapshot && (
        <p className="mt-3 text-[12.5px] text-[#6B6B6B] italic">
          No details yet — add the buyer, price, and go-home date so you can scan this sale at a glance.
        </p>
      )}

      {editing && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="sale-snapshot-form">
          <FieldInput label="Buyer name" value={snapDraft.buyer_name} onChange={(v) => setSnapDraft((d) => ({ ...d, buyer_name: v }))} />
          <FieldInput label="Buyer email" type="email" value={snapDraft.buyer_email} onChange={(v) => setSnapDraft((d) => ({ ...d, buyer_email: v }))} />
          <FieldInput label="Price ($)" type="number" value={snapDraft.price} onChange={(v) => setSnapDraft((d) => ({ ...d, price: v }))} />
          <FieldInput label="Deposit paid ($)" type="number" value={snapDraft.deposit_paid} onChange={(v) => setSnapDraft((d) => ({ ...d, deposit_paid: v }))} />
          <FieldInput label="Balance due ($)" type="number" value={snapDraft.balance_due} onChange={(v) => setSnapDraft((d) => ({ ...d, balance_due: v }))} />
          <FieldInput label="Go-home date" type="date" value={snapDraft.go_home_date} onChange={(v) => setSnapDraft((d) => ({ ...d, go_home_date: v }))} />
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Notes</Label>
            <Textarea
              value={snapDraft.notes}
              onChange={(e) => setSnapDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Microchip #, health-guarantee window, any verbal commitments…"
              rows={2}
              data-testid="sale-snapshot-notes"
            />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <Button onClick={save} disabled={busy} className="bg-ink hover:bg-crimson text-white rounded-full" data-testid="sale-snapshot-save">
              <FloppyDisk size={13} weight="bold" /> Save
            </Button>
            <Button onClick={() => setSnapDraft(null)} variant="outline" className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SnapField({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold">{label}</p>
      <p className="text-[13px] text-ink mt-0.5 break-words">{value || "—"}</p>
    </div>
  );
}

function FieldInput({ label, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export { SaleSnapshotPanel as _SaleSnapshotPanelInternal };
